import React, { useCallback, useEffect, useState } from 'react';
import CollapsiblePanel from './CollapsiblePanel';
import './ItemStockingTable.css';

const NO_FILTERS = { purchasedPrice: 'all', roiMin: '', roiMax: '', inStockMin: '', inStockMax: '' };
const PAGE_SIZES = [25, 50, 100, 'all'];
const PAGE_SIZE_KEY = 'itemStockingPageSize';

function storedPageSize() {
  try {
    const stored = localStorage.getItem(PAGE_SIZE_KEY);
    return stored === 'all' ? 'all' : PAGE_SIZES.includes(Number(stored)) ? Number(stored) : 50;
  } catch {
    return 50;
  }
}

function ItemStockingTable({
  seedingItems,
  sortedItems,
  filters,
  setFilters,
  page,
  setPage,
  sortConfig,
  handleSort,
  getSortIcon,
  editingStock,
  editStockValue,
  setEditStockValue,
  startEditingStock,
  cancelEditingStock,
  handleUpdateTargetStock,
  handleRemoveItem,
  formatPrice,
  formatNumber,
  formatProfitPercentage,
  handleImportToSeed,
  handleExportSeedList,
  handleExportToBuy,
  handleExportToBuyForecast,
  handleExportListPrices,
  setShowAddModal,
  expandedRows,
  toggleRowExpanded,
  itemForecasts,
  loadingForecasts,
  fetchItemForecast,
  analysisItemId,
  onSelectItem,
  aggregatePeriod,
  collapsed,
  onToggle,
}) {
  const [pageSize, setPageSize] = useState(storedPageSize);

  useEffect(() => {
    try {
      localStorage.setItem(PAGE_SIZE_KEY, String(pageSize));
    } catch {
      // Storage unavailable; the setting just won't persist
    }
  }, [pageSize]);

  const perPage = pageSize === 'all' ? Math.max(sortedItems.length, 1) : pageSize;
  const pages = Math.max(1, Math.ceil(sortedItems.length / perPage));
  const currentPage = Math.min(page, pages); // removing items can shrink the last page away
  const pageStart = (currentPage - 1) * perPage;
  const pageItems = sortedItems.slice(pageStart, pageStart + perPage);

  // Helper function to format model names for display
  const formatModelName = (modelName) => {
    if (!modelName) return 'none';

    const modelMap = {
      'MovingAverageModel': 'Moving Average',
      'ExponentialSmoothingModel': 'Exponential Smoothing',
      'HoltWintersModel': 'Holt-Winters Seasonal',
      'LinearTrendModel': 'Linear Trend',
      'CrostonsModel': 'Croston\'s Method',
      'TSBModel': 'TSB Method',
      'SeasonalNaiveModel': 'Seasonal Naive',
      'RecentTrueAverage': 'Recent Average',
      'Error': 'Error',
      'none': 'None',
      'error': 'Error',
      'individual': 'Individual Model',
      'fallback': 'Fallback Method',
      'recent_average_fallback': 'Recent Average Fallback'
    };

    return modelMap[modelName] || modelName;
  };


  // Enhanced toggle function that fetches forecast when expanding
  const handleRowToggle = useCallback((itemId) => {
    const isExpanding = !expandedRows.has(itemId);
    toggleRowExpanded(itemId);

    if (isExpanding) {
      // Fetch forecast when expanding
      fetchItemForecast(itemId);
    }
  }, [expandedRows, toggleRowExpanded, fetchItemForecast]);


  // Sortable header cell; the active column is marked so its arrow stands out
  const sortHeader = (col, label, title, className = '') => (
    <th
      className={`sortable ${sortConfig.key === col ? 'sorted' : ''} ${className}`}
      onClick={() => handleSort(col)}
      title={title}
    >
      {label}{getSortIcon(col)}
    </th>
  );

  const stockPercent = item => (item.target_stock > 0 ? (item.current_stock / item.target_stock) * 100 : null);
  const setFilter = (key, value) => setFilters(prev => ({ ...prev, [key]: value }));
  const filtered = sortedItems.length !== seedingItems.length;

  return (
    <CollapsiblePanel
      title="Items"
      sub={`${seedingItems.length} tracked`}
      collapsed={collapsed}
      onToggle={onToggle}
      actions={(
        <>
          {/* the export buttons carry hook classes SeedingDetailPage uses to flash "Copied" */}
          <button onClick={handleImportToSeed} className="btn btn-ghost import-seed-btn" title="Import items from clipboard to seed list">
            Import
          </button>
          <button onClick={handleExportSeedList} className="btn btn-ghost export-seed-list-btn" title="Copy item names and target stock">
            Copy seed list
          </button>
          <button onClick={handleExportToBuy} className="btn btn-ghost export-buy-btn" title="Copy items needed to reach target stock">
            Copy buy list
          </button>
          <button onClick={handleExportToBuyForecast} className="btn btn-ghost export-buy-forecast-btn" title="Copy items needed to cover forecast demand">
            Copy buy list (forecast)
          </button>
          <button onClick={handleExportListPrices} className="btn btn-ghost export-list-prices-btn" title="Copy list prices for the filtered items">
            Copy list prices
          </button>
          <button onClick={() => setShowAddModal(true)} className="btn btn-primary">
            Add item
          </button>
        </>
      )}
    >
      <div className="toolbar">
        <label className="ist-filter">
          Paid price
          <select
            value={filters.purchasedPrice}
            onChange={(e) => setFilter('purchasedPrice', e.target.value)}
          >
            <option value="all">Any</option>
            <option value="with">Known</option>
            <option value="without">Unknown</option>
          </select>
        </label>
        <label className="ist-filter">
          ROI
          <input type="number" step="0.1" value={filters.roiMin} onChange={(e) => setFilter('roiMin', e.target.value)} placeholder="min" aria-label="Minimum ROI %" />
          <span>–</span>
          <input type="number" step="0.1" value={filters.roiMax} onChange={(e) => setFilter('roiMax', e.target.value)} placeholder="max" aria-label="Maximum ROI %" />
          <span>%</span>
        </label>
        <label className="ist-filter">
          In stock
          <input type="number" step="0.1" min="0" value={filters.inStockMin} onChange={(e) => setFilter('inStockMin', e.target.value)} placeholder="min" aria-label="Minimum in-stock %" />
          <span>–</span>
          <input type="number" step="0.1" min="0" value={filters.inStockMax} onChange={(e) => setFilter('inStockMax', e.target.value)} placeholder="max" aria-label="Maximum in-stock %" />
          <span>%</span>
        </label>
        <button onClick={() => setFilters(NO_FILTERS)} className="btn btn-ghost btn-sm">Clear filters</button>
        <div className="toolbar-end">
          <span className="toolbar-count">
            {filtered ? `${sortedItems.length} of ${seedingItems.length} shown` : `${seedingItems.length} items`}
          </span>
          {pages > 1 && (
            <span className="btn-row">
              <button className="btn btn-sm" onClick={() => setPage(currentPage - 1)} disabled={currentPage <= 1} aria-label="Previous page">‹</button>
              <span className="toolbar-count">Page {currentPage} of {pages}</span>
              <button className="btn btn-sm" onClick={() => setPage(currentPage + 1)} disabled={currentPage >= pages} aria-label="Next page">›</button>
            </span>
          )}
          <label className="ist-filter">
            Per page
            <select
              value={pageSize}
              onChange={e => {
                setPageSize(e.target.value === 'all' ? 'all' : Number(e.target.value));
                setPage(1);
              }}
            >
              {PAGE_SIZES.map(size => (
                <option key={size} value={size}>{size === 'all' ? 'All' : size}</option>
              ))}
            </select>
          </label>
        </div>
      </div>

      {seedingItems.length === 0 ? (
        <div className="table-empty">No items yet. Use "Add item" or "Import" to start a seed list.</div>
      ) : sortedItems.length === 0 ? (
        <div className="table-empty">No items match these filters.</div>
      ) : (
        <>
          <div className="table-scroll">
            <table className="data-table ist-table">
              <thead>
                <tr className="ist-groups">
                  <th colSpan="3"></th>
                  <th colSpan="3" className="ist-group" style={{ '--g': 'var(--accent)' }}>Stock</th>
                  <th colSpan="4" className="ist-group" style={{ '--g': 'var(--violet)' }}>Demand</th>
                  <th colSpan="3" className="ist-group" style={{ '--g': 'var(--area-contracts)' }}>Market</th>
                  <th colSpan="2" className="ist-group" style={{ '--g': 'var(--warn)' }}>Your cost</th>
                  <th></th>
                </tr>
                <tr>
                  <th className="ist-expand" aria-label="Details"></th>
                  {sortHeader('item_name', 'Item', 'Sort by item name')}
                  {sortHeader('to_buy', 'Buy', 'Units needed to reach target stock', 'num')}
                  {sortHeader('current_stock', 'In stock', 'Units listed at this destination', 'num ist-first')}
                  {sortHeader('target_stock', 'Target', 'Target stock; click a value to edit', 'num')}
                  {sortHeader('stock_percentage', 'Stock', 'Stock remaining as a share of target', 'num')}
                  {sortHeader('avg_consumption', 'Avg. consumption', `Average units sold per ${aggregatePeriod}-day period`, 'num ist-first')}
                  {sortHeader('days_of_cover', 'Cover', 'Days a full target stock lasts at your recent sales rate here', 'num')}
                  {sortHeader('last_restock_days_ago', 'Last restock', 'Days since you last bought this item, and how many (any location)', 'num')}
                  {sortHeader('restock_interval_days', 'Every', 'Median days between your purchases of this item', 'num')}
                  {sortHeader('source_price', 'Source', 'Price at the source market', 'num ist-first')}
                  {sortHeader('current_price', 'Destination', 'Lowest sell price at this destination', 'num')}
                  {sortHeader('profit_percentage', 'ROI', 'Return after shipping, broker fee, sales tax and SCC surcharge (structures only)', 'num')}
                  {sortHeader('purchased_price', 'Paid', 'What you paid, from wallet history', 'num ist-first')}
                  {sortHeader('list_price', 'List at', 'Price that meets your target ROI', 'num')}
                  <th aria-label="Actions"></th>
                </tr>
              </thead>
              <tbody>
                {pageItems.map((item) => {
                  const expanded = expandedRows.has(item.id);
                  const pct = stockPercent(item);
                  return (
                    <React.Fragment key={item.id}>
                      <tr
                        className={[expanded && 'ist-row-open', item.id === analysisItemId && 'ist-row-selected'].filter(Boolean).join(' ')}
                        // The row's own controls keep their behaviour; anywhere else selects it for analysis
                        onClick={(e) => !e.target.closest('button, input, a') && onSelectItem(item)}
                      >
                        <td className="ist-expand">
                          <button
                            className="ist-expand-btn"
                            onClick={() => handleRowToggle(item.id)}
                            aria-expanded={expanded}
                            title="Show demand analysis and forecast"
                          >
                            {expanded ? '▾' : '▸'}
                          </button>
                        </td>
                        <td className="ist-name">
                          <button className="ist-name-btn" onClick={() => onSelectItem(item)} title="Chart demand and forecasts">
                            {item.item_name}
                          </button>
                        </td>
                        <td className="num">{formatNumber(Math.max(0, item.target_stock - item.current_stock))}</td>
                        <td className="num ist-first">{formatNumber(item.current_stock)}</td>
                        <td className="num">
                          {editingStock === item.id ? (
                            <span className="ist-edit">
                              <input
                                type="number"
                                value={editStockValue}
                                onChange={(e) => setEditStockValue(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') handleUpdateTargetStock(item.id, editStockValue);
                                  else if (e.key === 'Escape') cancelEditingStock();
                                }}
                                autoFocus
                                aria-label="Target stock"
                              />
                              <button onClick={() => handleUpdateTargetStock(item.id, editStockValue)} className="btn btn-primary btn-sm">Save</button>
                              <button onClick={cancelEditingStock} className="btn btn-ghost btn-sm">Cancel</button>
                            </span>
                          ) : (
                            <span className="ist-target">
                              <button className="ist-editable" onClick={() => startEditingStock(item)} title="Edit target stock">
                                {formatNumber(item.target_stock)}
                              </button>
                              {item.restock_status && (
                                <button
                                  className={`ist-suggest ${item.restock_status}`}
                                  onClick={() => startEditingStock(item, item.suggested_target ?? 0)}
                                  title={item.suggested_target == null
                                    ? `No sales here in the purchase lookback window, and not restocked for ${item.last_restock_days_ago} days. Click to edit.`
                                    : `Your sales here suggest ${formatNumber(item.suggested_target)} for your restock cadence. Click to use it.`}
                                >
                                  {item.restock_status === 'raise' ? '↑' : '↓'} {item.suggested_target == null ? 'idle' : formatNumber(item.suggested_target)}
                                </button>
                              )}
                            </span>
                          )}
                        </td>
                        <td className="num">
                          {pct == null ? <span className="text-faint">—</span> : (
                            <span className="ist-stock">
                              {pct.toFixed(0)}%
                              <span className="ist-bar">
                                <i className={pct >= 50 ? 'ok' : pct >= 10 ? 'mid' : ''} style={{ width: `${Math.min(100, Math.max(pct, 4))}%` }} />
                              </span>
                            </span>
                          )}
                        </td>
                        <td className="num ist-first">{formatNumber(item.avg_consumption || 0)}</td>
                        <td className="num" title={`Selling ${item.daily_sales}/day here`}>
                          {item.days_of_cover == null ? <span className="text-faint">No sales</span> : `${item.days_of_cover}d`}
                        </td>
                        <td className="num">
                          {item.last_restock_days_ago == null
                            ? <span className="text-faint">Never</span>
                            : <>{item.last_restock_days_ago}d ago <span className="text-faint">· {formatNumber(item.last_restock_quantity)}</span></>}
                        </td>
                        <td className="num">{item.restock_interval_days == null ? <span className="text-faint">—</span> : `${item.restock_interval_days}d`}</td>
                        <td className="num ist-first">{formatPrice(item.source_price)}</td>
                        <td className="num">{formatPrice(item.current_price)}</td>
                        <td className="num">{formatProfitPercentage(item)}</td>
                        <td className="num ist-first">{formatPrice(item.purchased_price)}</td>
                        <td className="num">{formatPrice(item.list_price)}</td>
                        <td className="num">
                          <button onClick={() => handleRemoveItem(item.id)} className="btn btn-danger btn-sm row-action">
                            Remove
                          </button>
                        </td>
                      </tr>
                      {expanded && (
                        <tr className="ist-detail">
                          <td colSpan="16">
                            <div className="ist-detail-grid">
                              <div className="ist-metric"><span>Average demand interval</span><b>{formatNumber(item.adi || 0)}</b></div>
                              <div className="ist-metric"><span>CV²</span><b>{(item.daily_cv_squared || 0).toFixed(4)}</b></div>
                              <div className="ist-metric"><span>Demand type</span><b>{item.daily_demand_type || 'Unknown'}</b></div>
                              {loadingForecasts.has(item.id) ? (
                                <div className="ist-metric"><span>Forecast</span><b className="text-faint">Loading...</b></div>
                              ) : itemForecasts[item.id] ? (
                                <>
                                  <div className="ist-metric"><span>Forecast, next {aggregatePeriod} days</span><b className="text-pos">{formatNumber(itemForecasts[item.id].forecast_demand || 0)}</b></div>
                                  <div className="ist-metric">
                                    <span>Confidence range</span>
                                    <b>{formatNumber(itemForecasts[item.id].forecast_confidence_lower || 0)} – {formatNumber(itemForecasts[item.id].forecast_confidence_upper || 0)}</b>
                                  </div>
                                  <div className="ist-metric"><span>Model</span><b>{formatModelName(itemForecasts[item.id].forecast_best_model || itemForecasts[item.id].forecast_model_type)}</b></div>
                                  <div className="ist-metric"><span>Training data</span><b>{itemForecasts[item.id].forecast_data_points || 0} days</b></div>
                                </>
                              ) : (
                                <div className="ist-metric"><span>Forecast</span><b className="text-faint">Not loaded</b></div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
          {pages > 1 && (
            <div className="pager">
              <span>{`${pageStart + 1}–${pageStart + pageItems.length} of ${sortedItems.length}`}</span>
              <span className="toolbar-end">
                <button className="btn btn-sm" onClick={() => setPage(currentPage - 1)} disabled={currentPage <= 1}>Previous</button>
                <span>Page {currentPage} of {pages}</span>
                <button className="btn btn-sm" onClick={() => setPage(currentPage + 1)} disabled={currentPage >= pages}>Next</button>
              </span>
            </div>
          )}
        </>
      )}
    </CollapsiblePanel>
  );
}

export default ItemStockingTable;